import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Play, Upload, Wand2 } from "lucide-react";
import {
  api,
  type SpeechBlock,
  type SpeechSettings,
  type TranscribeReport,
} from "@/lib/api";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  Select,
  Spinner,
  Table,
  Td,
  Textarea,
  Th,
  Toolbar,
} from "@/components/ui";

/**
 * Speech settings for one company.
 *
 * The page has to answer two questions an administrator actually has: will
 * this company's announcements be spoken or cut together, and — if spoken —
 * what exactly will a caller hear. The second one is why the block wording is
 * editable here rather than hidden behind the blueprint: the words are the
 * announcement now, the way the recordings used to be.
 */
export function CompanySpeech({ companyId }: { companyId: number }) {
  const qc = useQueryClient();
  const key = ["company", companyId, "speech"];

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api<SpeechSettings>(`/api/admin/companies/${companyId}/speech`),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{String(error)}</ErrorNote>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <EngineNote settings={data} />
      <ModeCard companyId={companyId} settings={data} onSaved={() => qc.invalidateQueries({ queryKey: key })} />
      <ReferenceCard companyId={companyId} settings={data} onSaved={() => qc.invalidateQueries({ queryKey: key })} />
      <BlockTexts companyId={companyId} />
      <PronunciationTester companyId={companyId} disabled={!data.engine.configured} />
    </div>
  );
}

/** What this installation can actually do, said plainly rather than implied. */
function EngineNote({ settings }: { settings: SpeechSettings }) {
  const e = settings.engine;
  if (!e.configured) {
    return <Notice tone="warning">{e.note}</Notice>;
  }
  return (
    <Notice tone={e.supportsClone ? "info" : "warning"}>
      <div className="flex flex-wrap items-center gap-2">
        <span>
          Engine <strong>{e.name}</strong>
          {e.model ? ` · Modell ${e.model}` : ""}
          {e.version ? ` · Version ${e.version}` : ""}
        </span>
        <Badge tone={e.supportsClone ? "success" : "neutral"}>
          {e.supportsClone ? "Stimmklon möglich" : "kein Stimmklon"}
        </Badge>
      </div>
      {e.note && <p className="mt-1 text-sm opacity-80">{e.note}</p>}
    </Notice>
  );
}

function ModeCard({
  companyId,
  settings,
  onSaved,
}: {
  companyId: number;
  settings: SpeechSettings;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);

  const save = useMutation({
    mutationFn: () =>
      api<SpeechSettings>(`/api/admin/companies/${companyId}/speech`, {
        method: "PUT",
        body: form,
      }),
    onSuccess: onSaved,
  });

  const spoken = form.mode === "tts";

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-medium">Betriebsart</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Ansagen erzeugen"
          hint={
            spoken
              ? "Der ganze Ansagetext wird in einem Stück gesprochen. Die Hintergrundmusik wird wie bisher daruntergelegt."
              : "Aus den aufgenommenen Bausteinen zusammengeschnitten, wie bisher."
          }
        >
          <Select
            value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value })}
            disabled={!settings.engine.configured && form.mode !== "tts"}
          >
            <option value="snippets">Bausteine (Aufnahmen)</option>
            <option value="tts" disabled={!settings.engine.configured}>
              Sprachsynthese
            </option>
          </Select>
        </Field>

        <Field label="Sprechtempo" hint="1,0 ist das Tempo der Stimme selbst.">
          <Input
            type="number"
            step="0.05"
            min="0.5"
            max="2"
            value={form.speed}
            onChange={(e) => setForm({ ...form, speed: Number(e.target.value) })}
          />
        </Field>

        <Field label="Anrede männlich" hint="Steht vor dem Namen. Leer lassen, wenn die Firma keine verwendet.">
          <Input
            value={form.maleSalutation}
            placeholder="Herr"
            onChange={(e) => setForm({ ...form, maleSalutation: e.target.value })}
          />
        </Field>
        <Field label="Anrede weiblich">
          <Input
            value={form.femaleSalutation}
            placeholder="Frau"
            onChange={(e) => setForm({ ...form, femaleSalutation: e.target.value })}
          />
        </Field>

        <Field
          label="Kurze Pause (ms)"
          hint="Pausen im Bauplan werden zu Satzzeichen. Kürzer als das wird ein Komma."
        >
          <Input
            type="number"
            value={form.shortPauseMs || 250}
            onChange={(e) => setForm({ ...form, shortPauseMs: Number(e.target.value) })}
          />
        </Field>
        <Field label="Mittlere Pause (ms)" hint="Darunter ein Punkt, darüber eine längere Pause.">
          <Input
            type="number"
            value={form.mediumPauseMs || 600}
            onChange={(e) => setForm({ ...form, mediumPauseMs: Number(e.target.value) })}
          />
        </Field>
      </div>

      {spoken && (
        <label className="mt-3 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.requireClone}
            onChange={(e) => setForm({ ...form, requireClone: e.target.checked })}
          />
          <span>
            Nur die eigene Stimme — kann die Engine die hinterlegte
            Referenzstimme nicht verwenden, wird die Ansage verweigert statt in
            der Standardstimme gesprochen. Für Firmen, bei denen die Stimme Teil
            der Marke ist.
            <span className="mt-0.5 block text-xs muted">
              {settings.engine.supportsClone
                ? `${settings.engine.name} kann klonen — die Einstellung greift erst, wenn auf eine Engine ohne Stimmklon gewechselt wird.`
                : `${settings.engine.name} kann nicht klonen — ohne Haken werden Ansagen dieser Firma in der Standardstimme der Engine gesprochen.`}
            </span>
          </span>
        </label>
      )}

      {save.error && <ErrorNote>{String(save.error)}</ErrorNote>}
      <Toolbar>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Speichert…" : "Speichern"}
        </Button>
      </Toolbar>
    </Card>
  );
}

/**
 * The company's own speaker.
 *
 * The transcript is mandatory and the form says why: cloning aligns the voice
 * against the words, and a wrong transcript produces a voice that is audibly
 * not the person without any error appearing anywhere.
 */
function ReferenceCard({
  companyId,
  settings,
  onSaved,
}: {
  companyId: number;
  settings: SpeechSettings;
  onSaved: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [transcript, setTranscript] = useState(settings.cloneTranscript);
  useEffect(() => setTranscript(settings.cloneTranscript), [settings.cloneTranscript]);

  // Cache-busted on the checksum: a re-upload keeps the same URL, and the
  // browser would otherwise play back the recording that was just replaced.
  const referenceSrc =
    `/api/admin/companies/${companyId}/speech/reference/audio` +
    `?v=${settings.cloneSha256.slice(0, 12)}`;

  const upload = useMutation({
    mutationFn: async () => {
      const file = fileInput.current?.files?.[0];
      if (!file) throw new Error("Bitte eine Aufnahme auswählen.");
      const form = new FormData();
      form.append("file", file, file.name);
      form.append("transcript", transcript);
      const res = await fetch(`/api/admin/companies/${companyId}/speech/reference`, {
        method: "POST",
        body: form,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrf() },
      });
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as SpeechSettings;
    },
    onSuccess: onSaved,
  });

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium">Referenzstimme</h3>
      <p className="mb-3 text-sm muted">Eine vollständige, ruhig gesprochene Aufnahme von 5 bis 30 Sekunden.</p>
      <Notice tone="info">
        Nehmt einen <strong>ganzen Satz</strong>, keinen Baustein. Bausteine sind mit
        weiterführender Betonung aufgenommen — „ist derzeit nicht erreichbar" beginnt
        mitten im Satz und endet schwebend. Der Klon erbt das und jede Ansage klingt,
        als käme sie nicht zu Ende.
      </Notice>

      {settings.cloneKey && !settings.engine.supportsClone && !settings.requireClone && (
        <Notice tone="warning">
          Diese Engine kann die hinterlegte Stimme nicht verwenden. Ansagen werden
          trotzdem erzeugt — in der Standardstimme der Engine, und jede Vorschau
          sagt das dazu. Wer lieber gar keine Ansage möchte als eine in fremder
          Stimme, schaltet unter <strong>Betriebsart</strong> „nur die eigene
          Stimme" ein.
        </Notice>
      )}

      {settings.cloneKey && settings.cloneBytes === 0 ? (
        <ErrorNote>
          Für diese Firma ist eine Referenzstimme eingetragen, die Datei fehlt aber
          auf der Platte ({settings.cloneKey}). Bitte neu hochladen —
          bis dahin verweigern Ansagen dieser Firma die Synthese.
        </ErrorNote>
      ) : settings.cloneKey ? (
        <div className="my-3 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="success">Referenzstimme hinterlegt</Badge>
            <a
              className="inline-flex items-center gap-1 text-sm underline"
              href={`${referenceSrc}&download=1`}
              download
            >
              <Download size={14} /> Herunterladen
            </a>
          </div>
          <AudioPlayer src={referenceSrc} />
          <p className="text-xs opacity-70">
            {settings.cloneKey} · {(settings.cloneBytes / 1024).toFixed(0)} kB · Prüfsumme{" "}
            {settings.cloneSha256.slice(0, 12)}
          </p>
        </div>
      ) : (
        <Empty title="Noch keine Referenzstimme hinterlegt." />
      )}

      <Field
        label="Transkript der Aufnahme"
        hint="Wort für Wort, so wie es gesprochen wird. Der Klon richtet sich danach aus."
      >
        <Textarea
          rows={2}
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Willkommen bei gosh audio. Ich bin derzeit nicht erreichbar."
        />
      </Field>

      <input ref={fileInput} type="file" accept="audio/wav,audio/mpeg,.wav,.mp3" className="mt-2 text-sm" />

      {upload.error && <ErrorNote>{String(upload.error)}</ErrorNote>}
      <Toolbar>
        <Button variant="primary" onClick={() => upload.mutate()} disabled={upload.isPending || !transcript.trim()}>
          <Upload size={16} /> {upload.isPending ? "Lädt hoch…" : "Referenz hochladen"}
        </Button>
      </Toolbar>
    </Card>
  );
}

/**
 * What each building block says, for this company.
 *
 * The wording is per tenant because it genuinely differs: the shared
 * description of "farewell_" reads "auf Wiedersehen", while the recording one
 * customer actually made says "Vielen Dank."
 */
function BlockTexts({ companyId }: { companyId: number }) {
  const qc = useQueryClient();
  const key = ["company", companyId, "speech", "blocks"];

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api<SpeechBlock[]>(`/api/admin/companies/${companyId}/speech/blocks`),
  });

  const transcribe = useMutation({
    mutationFn: () =>
      api<TranscribeReport>(`/api/admin/companies/${companyId}/speech/transcribe`, {
        method: "POST",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{String(error)}</ErrorNote>;

  const blocks = data ?? [];
  const unconfirmed = blocks.filter((b) => b.text && !b.confirmed).length;

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium">Bausteintexte</h3>
      <p className="mb-3 text-sm muted">Was jeder Baustein sagt. Diese Texte werden gesprochen, nicht die Dateinamen.</p>
      <Toolbar>
        <Button
          variant="secondary"
          onClick={() => transcribe.mutate()}
          disabled={transcribe.isPending}
        >
          <Wand2 size={16} />
          {transcribe.isPending ? "Hört zu…" : "Aus Aufnahmen übernehmen"}
        </Button>
        {unconfirmed > 0 && <Badge tone="warning">{unconfirmed} ungeprüft</Badge>}
      </Toolbar>

      {transcribe.error && <ErrorNote>{String(transcribe.error)}</ErrorNote>}
      {transcribe.data && (
        <Notice tone="info">
          {transcribe.data.transcribed} aus Aufnahmen übernommen,{" "}
          {transcribe.data.fromFallback} aus der allgemeinen Beschreibung,{" "}
          {transcribe.data.skipped} übersprungen. Bitte durchsehen — die Erkennung
          irrt sich hörbar oft, gerade bei Uhrzeiten.
          {transcribe.data.notes?.map((n) => (
            <div key={n} className="mt-1 text-xs opacity-80">
              {n}
            </div>
          ))}
        </Notice>
      )}

      {blocks.length === 0 ? (
        <Empty title="Diese Firma verwendet keine Bausteine." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Baustein</Th>
              <Th>Gesprochener Text</Th>
              <Th>Herkunft</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {blocks.map((b) => (
              <BlockRow key={b.blockId} companyId={companyId} block={b} />
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function BlockRow({ companyId, block }: { companyId: number; block: SpeechBlock }) {
  const qc = useQueryClient();
  const [text, setText] = useState(block.text || block.fallback);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setText(block.text || block.fallback);
    setDirty(false);
  }, [block.text, block.fallback]);

  const save = useMutation({
    mutationFn: (confirmed: boolean) =>
      api<void>(`/api/admin/companies/${companyId}/speech/blocks/${block.blockId}`, {
        method: "PUT",
        body: { text, confirmed },
      }),
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["company", companyId, "speech", "blocks"] });
    },
  });

  // A block that names a person or a pause never carries wording of its own.
  const structural = block.variable === "user_id" || block.variable === "proxy_user_id";

  return (
    <tr>
      <Td>
        <div className="font-mono text-xs">{block.namePattern}</div>
        <div className="text-xs opacity-70">{block.description}</div>
      </Td>
      <Td>
        <Textarea
          rows={2}
          value={text}
          placeholder={structural ? "Der Name wird hier eingesetzt, z. B. [SEX] [USERID]" : "Wird nicht gesprochen"}
          onChange={(e) => {
            setText(e.target.value);
            setDirty(true);
          }}
        />
      </Td>
      <Td>
        {block.confirmed ? (
          <Badge tone="success">geprüft</Badge>
        ) : block.source === "asr" ? (
          <Badge tone="warning">erkannt</Badge>
        ) : block.source === "description" ? (
          <Badge tone="neutral">Beschreibung</Badge>
        ) : block.text ? (
          <Badge tone="neutral">manuell</Badge>
        ) : (
          <Badge tone="neutral">leer</Badge>
        )}
      </Td>
      <Td>
        <div className="flex gap-1">
          <Button size="sm" variant="secondary" onClick={() => save.mutate(false)} disabled={!dirty}>
            Speichern
          </Button>
          <Button size="sm" variant="primary" onClick={() => save.mutate(true)} disabled={save.isPending}>
            Passt
          </Button>
        </div>
      </Td>
    </tr>
  );
}

/**
 * Hearing a name before a caller does.
 *
 * The name is always spoken inside a sentence. On its own it gets its own
 * closing intonation and sounds wrong no matter how it is pronounced, which
 * would send people chasing a problem that is not there.
 */
function PronunciationTester({ companyId, disabled }: { companyId: number; disabled: boolean }) {
  const [name, setName] = useState("");
  const [src, setSrc] = useState("");

  return (
    <Card className="p-4">
      <h3 className="text-sm font-medium">Aussprache prüfen</h3>
      <p className="mb-3 text-sm muted">Der Name wird in einem Beispielsatz gesprochen.</p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Name oder Schreibweise">
          <Input
            value={name}
            placeholder="Dürhammer"
            onChange={(e) => setName(e.target.value)}
            disabled={disabled}
          />
        </Field>
        <Button
          variant="primary"
          disabled={disabled || !name.trim()}
          onClick={() =>
            setSrc(
              `/api/admin/companies/${companyId}/speech/pronounce?name=${encodeURIComponent(name)}&t=${Date.now()}`,
            )
          }
        >
          <Play size={16} /> Anhören
        </Button>
      </div>
      {src && (
        <div className="mt-3">
          <AudioPlayer src={src} />
        </div>
      )}
    </Card>
  );
}

function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)mbm_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "";
}
